import { useState } from 'react';
import { ContinueButton } from './questions/common.jsx';

// Assessment title slide.
export function Intro({ assessment, index, count, onBegin }) {
  return (
    <div style={{ paddingTop: 14 }}>
      <span style={{ fontSize: 13, color: 'var(--coral)', fontWeight: 600, letterSpacing: '.3px' }}>
        ASSESSMENT {index + 1} OF {count}
      </span>
      <h1 style={{ margin: '12px 0 12px' }}>{assessment.name}</h1>
      <p style={{ fontSize: 16, color: 'var(--muted)', lineHeight: 1.6, margin: '0 0 26px', maxWidth: 420 }}>
        {assessment.sub}
      </p>
      <ContinueButton label={index === 0 ? 'Begin' : 'Start assessment'} onClick={onBegin} />
    </div>
  );
}

// Deferred "secure your progress" step (password + optional mobile). Skippable.
// The actual set-password call is wired in the accounts step; here it just advances.
export function Secure({ onSave, onSkip, initialMobile = '' }) {
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState(initialMobile);
  return (
    <>
      <div className="icon-badge" style={{ background: 'var(--coral-tint)', color: 'var(--coral)' }}>🔒</div>
      <h1 style={{ margin: '0 0 8px' }}>Set a password? (optional)</h1>
      <p className="sub" style={{ margin: '0 0 20px', maxWidth: 440 }}>
        Totally optional — you can always return with just your email. Add a password only if
        you’d like to sign in that way too.
      </p>
      <label className="flab">Password</label>
      <input className="win" type="password" placeholder="At least 10 characters" value={password} onChange={(e) => setPassword(e.target.value)} />
      <p className="note">🛡️ Stored encrypted. We check it isn’t a known-breached password.</p>
      <label className="flab" style={{ marginTop: 14 }}>Mobile phone (recommended)</label>
      <input className="win" type="tel" placeholder="+1 (___) ___ ____" value={mobile} onChange={(e) => setMobile(e.target.value)} />
      <p className="note">💬 Used only to help you recover your account by text.</p>
      <div className="spacer" />
      <ContinueButton label="Save password" onClick={() => onSave({ password, mobile })} disabled={password.length < 10} />
      <button className="ghost" onClick={onSkip}>Skip — I’ll use my email</button>
    </>
  );
}

export function Saved({ onResume, email = '' }) {
  const resumeUrl = email ? `${window.location.origin}/?email=${encodeURIComponent(email)}` : '';
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div className="icon-badge" style={{ background: 'var(--ok-tint)', color: 'var(--ok)', margin: '0 auto 18px', width: 56, height: 56, fontSize: 30 }}>✓</div>
      <h1 style={{ margin: '0 0 8px' }}>Progress saved</h1>
      <p className="sub" style={{ margin: '0 0 20px', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
        Come back anytime — re-open with your email{email ? <> (<strong>{email}</strong>)</> : ''} and you’ll
        pick up right where you left off. Bookmark your link below so it’s one click.
      </p>
      <div style={{ maxWidth: 380, margin: '0 auto' }}>
        <ContinueButton label="Keep going" onClick={onResume} />
        {resumeUrl && (
          <div style={{ marginTop: 16, textAlign: 'left' }}>
            <label className="flab">Or bookmark your resume link</label>
            <input className="win" readOnly value={resumeUrl} onFocus={(e) => e.target.select()} />
          </div>
        )}
      </div>
    </div>
  );
}

export function SignIn({ email = '', onSignIn }) {
  const [value, setValue] = useState(email);
  const [password, setPassword] = useState('');
  return (
    <div style={{ maxWidth: 340, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, margin: '10px 0 22px' }}>
        <div className="ppmark">P</div>
        <div className="ppwm"><span className="a">POMPA</span> <span className="b">PROGRAM</span></div>
      </div>
      <h1 style={{ fontSize: 24, margin: '0 0 8px', textAlign: 'center' }}>Welcome back</h1>
      <p className="sub" style={{ textAlign: 'center' }}>Sign in to pick up where you left off.</p>
      <label className="flab">Email</label>
      <input className="win" type="email" value={value} onChange={(e) => setValue(e.target.value)} />
      <label className="flab" style={{ marginTop: 12 }}>Password</label>
      <input className="win" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
      <div className="spacer" />
      <ContinueButton label="Sign in" onClick={() => onSignIn({ email: value, password })} />
    </div>
  );
}

export function Done({ assessments = [] }) {
  const names = assessments.map((a) => a.name);
  const list =
    names.length > 1
      ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
      : names[0] || '';
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div className="icon-badge" style={{ background: 'var(--accent-tint)', color: 'var(--accent)', margin: '0 auto 18px', width: 56, height: 56, fontSize: 32 }}>✓</div>
      <h1 style={{ margin: '0 0 6px' }}>Thank you!</h1>
      <p style={{ margin: '0 0 14px', fontSize: 17, fontWeight: 500, color: 'var(--slate)' }}>
        {names.length ? `All ${names.length} assessments are complete.` : 'You’re all done.'}
      </p>
      <p className="sub" style={{ margin: 0 }}>
        {list ? `Your ${list} are saved.` : 'Your answers are saved.'}
      </p>
    </div>
  );
}

// Shown at entry when /leads can't find a purchase for the email (soft gate — the user
// can fix the email or continue anyway).
export function ConfirmPurchase({ email, onContinue, onEdit, busy }) {
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div className="icon-badge" style={{ background: 'var(--accent-tint)', color: 'var(--accent)', margin: '0 auto 18px', width: 56, height: 56, fontSize: 30 }}>?</div>
      <h1 style={{ margin: '0 0 8px' }}>We couldn’t find your purchase</h1>
      <p className="sub" style={{ margin: '0 auto 22px', maxWidth: 420 }}>
        We don’t see an order under <strong>{email}</strong>. Double-check it’s the email you used
        at checkout — or continue and we’ll help sort it out.
      </p>
      <div style={{ maxWidth: 320, margin: '0 auto' }}>
        <ContinueButton label={busy ? 'One sec…' : 'Continue anyway'} onClick={onContinue} disabled={busy} />
        <button className="ghost" onClick={onEdit}>Edit my email</button>
      </div>
    </div>
  );
}

// Shown at the end for a "multiple"-plan buyer: collect the family member's email so they
// can take their own assessment.
export function FamilyMember({ onAdd, onSkip, busy }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(null); // { email, url, emailed }
  const [error, setError] = useState('');
  const valid = /.+@.+\..+/.test(email);

  async function invite() {
    setError('');
    const res = await onAdd(email);
    if (res && res.info) setSent(res.info);
    else setError((res && res.error) || 'We couldn’t send that invite. You can finish without inviting.');
  }

  if (sent) {
    return (
      <div style={{ textAlign: 'center', padding: '16px 0' }}>
        <div className="icon-badge" style={{ background: 'var(--accent-tint)', color: 'var(--accent)', margin: '0 auto 18px', width: 56, height: 56, fontSize: 30 }}>✓</div>
        <h1 style={{ margin: '0 0 8px' }}>Invite sent to {sent.email}</h1>
        <p className="sub" style={{ margin: '0 auto 18px', maxWidth: 440 }}>
          {sent.emailed
            ? 'We emailed them a link to take their own assessment. You can also share it directly:'
            : 'Share this link so they can take their own assessment:'}
        </p>
        <div style={{ maxWidth: 420, margin: '0 auto', textAlign: 'left' }}>
          <label className="flab">Their invite link</label>
          <input className="win" readOnly value={sent.url} onFocus={(e) => e.target.select()} />
          <div className="spacer" style={{ height: 16 }} />
          <ContinueButton label="Done" onClick={onSkip} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div className="icon-badge" style={{ background: 'var(--accent-tint)', color: 'var(--accent)', margin: '0 auto 18px', width: 56, height: 56, fontSize: 30 }}>👪</div>
      <h1 style={{ margin: '0 0 8px' }}>Your purchase includes a family member</h1>
      <p className="sub" style={{ margin: '0 auto 20px', maxWidth: 440 }}>
        Enter their email and we’ll invite them to take their own assessment.
      </p>
      <div style={{ maxWidth: 340, margin: '0 auto', textAlign: 'left' }}>
        <label className="flab">Family member’s email</label>
        <input className="win" type="email" placeholder="them@example.com" value={email} onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }} />
        {error && <p className="note" style={{ color: 'var(--coral)' }}>⚠️ {error}</p>}
        <div className="spacer" style={{ height: 16 }} />
        <ContinueButton label={busy ? 'Sending…' : 'Send invite'} onClick={invite} disabled={!valid || busy} />
        <button className="ghost" onClick={onSkip}>{error ? 'Finish without inviting →' : 'Skip for now'}</button>
      </div>
    </div>
  );
}
